import { useLayoutEffect, useRef, useState } from 'react';

import type { RefObject } from 'react';

/**
 * Placement for a `position: fixed` popover anchored to an element — the
 * translation editors (`ReplaceSpanEditor`, `TranslatedPhraseEditorPopover`)
 * and any future anchored editor that is NOT a select-style dropdown (those use
 * `useAnchoredDropdown`, which also owns dismissal and sizes to its trigger).
 *
 * The one rule this exists to enforce (PLAT-11479): an anchored popover is
 * placed against its **measured** height, never a hardcoded estimate. Estimates
 * are wrong the moment a section expands, a mode toggle or a notice renders, and
 * a wrong estimate lands the popover's bottom off-screen — buttons and the
 * input the user came for gone. So the hook opens on the estimate (so the first
 * paint is close), then re-places against the rendered content in a layout
 * effect before paint, and keeps doing so while the content resizes.
 *
 * Placement order:
 *   1. below the anchor when the content fits there;
 *   2. otherwise above it when it fits there;
 *   3. otherwise the roomier side, with the content scrolling inside the
 *      popover (`maxHeight` is set for this — the wrapper must apply it with
 *      `overflow-y: auto`);
 *   4. unless even the roomier side is too cramped to work in, in which case
 *      the popover overlaps the anchor and takes the viewport height.
 * A popover that carries its own copy of the anchored words (the Redub
 * Selection popover quotes the passage, PLAT-12042) can ask for step 4 as
 * soon as it would have to scroll (`coverAnchorWhenScrolling`): it has
 * nothing to keep visible beside itself, and a taller popover beats a
 * scrolling one.
 * Horizontally it aligns to the anchor's left edge, clamped into the viewport.
 */
export interface AnchoredPopoverPlacement {
  left: number;
  top: number;
  /** Inline cap — with `overflow-y: auto` the content scrolls past it. */
  maxHeight: number;
}

interface RectLike {
  top: number;
  bottom: number;
  left: number;
}

interface Size {
  width: number;
  height: number;
}

/** Gap kept between the popover and both the anchor and the viewport edges. */
export const ANCHORED_POPOVER_MARGIN = 8;

/**
 * Below this much room, a side is not worth scrolling an editor inside — the
 * popover overlaps its anchor instead (step 4 above). An editor squeezed into a
 * 100px strip is less usable than one covering the words it edits.
 */
const MIN_USABLE_HEIGHT = 240;

function readViewport(): Size {
  return { width: window.innerWidth, height: window.innerHeight };
}

export function computeAnchoredPopoverPlacement(
  anchor: RectLike,
  popover: Size,
  viewport: Size,
  margin: number = ANCHORED_POPOVER_MARGIN,
  coverAnchorWhenScrolling = false,
): AnchoredPopoverPlacement {
  const left = Math.max(
    margin,
    Math.min(anchor.left, viewport.width - popover.width - margin),
  );
  // Room for the popover on each side, after the gap to the anchor AND the
  // gap to the viewport edge.
  const spaceBelow = viewport.height - anchor.bottom - 2 * margin;
  const spaceAbove = anchor.top - 2 * margin;

  if (popover.height <= spaceBelow) {
    return { left, top: anchor.bottom + margin, maxHeight: spaceBelow };
  }
  if (popover.height <= spaceAbove) {
    return {
      left,
      top: anchor.top - margin - popover.height,
      maxHeight: spaceAbove,
    };
  }

  const room = Math.max(spaceBelow, spaceAbove);
  if (!coverAnchorWhenScrolling && room >= MIN_USABLE_HEIGHT) {
    return spaceBelow >= spaceAbove
      ? { left, top: anchor.bottom + margin, maxHeight: spaceBelow }
      : { left, top: margin, maxHeight: spaceAbove };
  }

  const maxHeight = Math.max(0, viewport.height - 2 * margin);
  const height = Math.min(popover.height, maxHeight);
  const top = Math.max(margin, viewport.height - margin - height);
  return { left, top, maxHeight };
}

function samePlacement(
  a: AnchoredPopoverPlacement,
  b: AnchoredPopoverPlacement,
): boolean {
  return a.left === b.left && a.top === b.top && a.maxHeight === b.maxHeight;
}

interface AnchoredPopoverOptions {
  /** The popover's CSS width (px) — used for the horizontal clamp. */
  width: number;
  /**
   * Height to place with before the first measurement. Only shapes the very
   * first paint; the layout effect re-places against the real height before
   * the user sees it.
   */
  estimatedHeight: number;
  /**
   * When the content fits neither side whole, cover the anchor and take the
   * viewport height instead of scrolling inside the roomier side. For a
   * popover that shows the anchored words itself (see the module note).
   */
  coverAnchorWhenScrolling?: boolean;
}

/**
 * Anchored, measured, viewport-clamped placement — see the module note.
 *
 * The anchor's rect is captured once, on open: the popover is `position: fixed`
 * and deliberately stays where it opened even if the surface behind it scrolls
 * (re-anchoring every frame would fight that scroll). Only the popover's own
 * size is re-observed.
 */
export function useAnchoredPopoverPlacement(
  anchorElement: HTMLElement,
  popoverRef: RefObject<HTMLElement>,
  options: AnchoredPopoverOptions,
): AnchoredPopoverPlacement {
  const anchorRectRef = useRef<RectLike | null>(null);
  const [placement, setPlacement] = useState<AnchoredPopoverPlacement>(() => {
    const rect = anchorElement.getBoundingClientRect();
    anchorRectRef.current = rect;
    return computeAnchoredPopoverPlacement(
      rect,
      { width: options.width, height: options.estimatedHeight },
      readViewport(),
      ANCHORED_POPOVER_MARGIN,
      options.coverAnchorWhenScrolling,
    );
  });

  useLayoutEffect(() => {
    const el = popoverRef.current;
    const anchorRect = anchorRectRef.current;
    if (!el || !anchorRect) return;
    const measure = () => {
      // The natural content height: `scrollHeight` sees past an applied
      // `maxHeight` clamp, `offsetHeight` adds the borders when nothing clamps.
      const height = Math.max(el.scrollHeight, el.offsetHeight);
      const next = computeAnchoredPopoverPlacement(
        anchorRect,
        { width: el.offsetWidth || options.width, height },
        readViewport(),
        ANCHORED_POPOVER_MARGIN,
        options.coverAnchorWhenScrolling,
      );
      setPlacement(prev => (samePlacement(prev, next) ? prev : next));
    };
    measure();
    // Content changes while open (a disclosure expands, a mode switches, a
    // notice appears) re-place the popover so it never grows off-screen.
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [popoverRef, options.width, options.coverAnchorWhenScrolling]);

  return placement;
}
